import { Document, Page, Text, View, StyleSheet, Image } from "@react-pdf/renderer";
import { getReactPdfColors } from "@/theme/reactPdfColors";
import { readStoredTenantBrandingIdentity } from "@/utils/tenant-branding-cache";
import "./register-fonts";

const styles = StyleSheet.create({
  page: {
    backgroundColor: "white",
    fontSize: 12,
  },
  logo: {
    paddingLeft: "10px",
    paddingBottom: "5px",
    width: 55,
  },
  passWrapper: {
    margin: "10 auto",
    border: "3px solid black",
    width: "50%",
  },
  passHead: {
    display: "flex",
    flexDirection: "column",
    height: 60,
  },
  passHeadMain: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    flexGrow: 1,
  },
  headerTextWrapper: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    flexGrow: 1,
  },
  headTextCommon: {
    alignSelf: "center",
    fontFamily: "Roboto-Bold",
    fontSize: "12px",
  },
  headText: {
    fontSize: "14px",
    letterSpacing: "1px",
  },
  mehmaanStrip: {
    height: 16,
    borderTop: "1px solid black",
    alignItems: "center",
    justifyContent: "center",
  },
  mehmaanStripText: {
    fontFamily: "Roboto-Bold",
    fontSize: "9px",
    letterSpacing: "0.5px",
    textAlign: "center",
  },
  tagBox: {
    border: "2px solid black",
    paddingVertical: "3px",
    paddingHorizontal: "5px",
  },
});

export type PassFamilyMember = {
  its: string | number;
  name: string;
  gender: string;
  age: string | number;
  hasChair?: boolean;
  isMehmaan?: boolean;
  jamaat?: string;
};

export type PassesProps = {
  familyMembers?: PassFamilyMember[];
  formNo: string | number;
  markaz: string;
  markazLabel?: string;
  namaazVenue: string;
  namaazVenueLabel?: string;
  isAshara?: boolean;
  event: { slug: string };
  /** Defaults to cached fields from `GET /tenant-branding/me`. */
  tenantName?: string;
  logoUrl?: string;
};

export function Passes({
  familyMembers = [],
  formNo,
  markaz,
  markazLabel,
  namaazVenue,
  namaazVenueLabel,
  isAshara = false,
  event,
  tenantName: tenantNameProp,
  logoUrl: logoUrlProp,
}: PassesProps) {
  const cached = readStoredTenantBrandingIdentity();
  const tenantName = tenantNameProp ?? cached.tenantName;
  const logoUrl = logoUrlProp ?? cached.logoUrl;
  const headerVenueLabel = markazLabel ?? markaz;
  const jamanLabel = markazLabel ?? markaz;
  const namaazLabel = namaazVenueLabel ?? namaazVenue;
  const pdfColors = getReactPdfColors();
  const headerBg = pdfColors.passZmBg;
  const headerFg = pdfColors.passZmFg;
  const mehmaanStripBg = pdfColors.passMehmaanBg;
  const mehmaanStripFg = pdfColors.passMehmaanFg;

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {familyMembers.map((fm, i) => (
          <View key={String(fm.its)} style={styles.passWrapper} break={i !== 0}>
            <View
              style={{
                ...styles.passHead,
                backgroundColor: headerBg,
              }}
            >
              <View style={styles.passHeadMain}>
                {logoUrl ? <Image src={logoUrl} style={styles.logo} /> : null}
                <View style={styles.headerTextWrapper}>
                  <Text
                    style={{
                      ...styles.headTextCommon,
                      ...styles.headText,
                      color: headerFg,
                    }}
                  >
                    {tenantName || "JMS"}
                  </Text>
                  <Text
                    style={{
                      ...styles.headTextCommon,
                      color: headerFg,
                    }}
                  >{`${headerVenueLabel} ${event.slug}`}</Text>
                </View>
              </View>
              {fm.isMehmaan ? (
                <View style={{ ...styles.mehmaanStrip, backgroundColor: mehmaanStripBg }}>
                  <Text style={{ ...styles.mehmaanStripText, color: mehmaanStripFg }}>MEHMAAN</Text>
                </View>
              ) : null}
            </View>
            <View
              style={{
                display: "flex",
                flexDirection: "column",
                padding: "5px",
              }}
            >
              <Text
                style={{
                  alignSelf: "flex-end",
                  paddingRight: "15px",
                  fontSize: "10px",
                  fontFamily: "Roboto-Bold",
                }}
              >{`Card # : ${formNo}/${i + 1}`}</Text>
              <View
                style={{
                  fontFamily: "Roboto-Bold",
                  fontSize: "13px",
                  flexDirection: "row",
                  alignItems: "center",
                }}
              >
                <Text style={{ width: "20%" }}>ITS ID</Text>
                <Text style={{ marginRight: "10px" }}>:</Text>
                <View
                  style={{
                    width: "80%",
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: fm.isMehmaan && fm.jamaat ? "space-between" : "flex-start",
                  }}
                >
                  <Text style={styles.tagBox}>{fm.its}</Text>
                  {fm.isMehmaan && fm.jamaat ? (
                    <Text style={styles.tagBox}>{fm.jamaat}</Text>
                  ) : null}
                </View>
              </View>
              <View
                style={{
                  paddingTop: "3px",
                  fontSize: "12px",
                  flexDirection: "row",
                  overflow: "hidden",
                }}
              >
                <Text style={{ width: "20%" }}>Name</Text>
                <Text style={{ marginRight: "10px" }}>:</Text>
                <Text style={{ width: "80%" }}>{fm.name}</Text>
              </View>
              <View
                style={{
                  paddingTop: "3px",
                  fontSize: "12px",
                  flexDirection: "row",
                }}
              >
                <Text style={{ width: "20%" }}>Gender</Text>
                <Text style={{ marginRight: "10px" }}>:</Text>
                <Text style={{ width: "80%" }}>{fm.gender}</Text>
              </View>
              <View
                style={{
                  paddingTop: "3px",
                  fontSize: "12px",
                  flexDirection: "row",
                }}
              >
                <Text style={{ width: "20%" }}>Age</Text>
                <Text style={{ marginRight: "10px" }}>:</Text>
                <Text style={{ width: "80%" }}>{fm.age}</Text>
              </View>
              {isAshara ? (
                <View
                  style={{
                    paddingTop: "3px",
                    fontSize: "12px",
                    flexDirection: "row",
                  }}
                >
                  <Text style={{ width: "20%" }}>Venue</Text>
                  <Text style={{ marginRight: "10px" }}>:</Text>
                  <Text style={{ width: "80%" }}>{headerVenueLabel}</Text>
                </View>
              ) : (
                <View
                  style={{
                    paddingTop: "3px",
                    fontSize: "12px",
                    flexDirection: "row",
                  }}
                >
                  <Text style={{ width: "20%" }}>Namaaz</Text>
                  <Text style={{ marginRight: "10px" }}>:</Text>
                  <Text style={{ width: "20%", marginRight: "10px" }}>{namaazLabel}</Text>
                  <Text style={{ width: "20%" }}>Jaman</Text>
                  <Text style={{ marginRight: "10px" }}>:</Text>
                  <Text style={{ width: "20%", marginRight: "10px" }}>{jamanLabel}</Text>
                </View>
              )}
              <View
                style={{
                  paddingTop: "15px",
                  flexDirection: "row",
                  justifyContent: "space-between",
                }}
              >
                <Text
                  style={{
                    fontFamily: "Roboto-Italic",
                    fontSize: "8px",
                  }}
                >
                  {fm.isMehmaan
                    ? "Non-Transferable, carry this card everyday - valid with original ITS."
                    : "Non-Transferable, carry this card everyday."}
                </Text>

                {fm.hasChair ? <Image src="/chair.png" style={{ height: "50px" }} /> : null}
              </View>
            </View>
          </View>
        ))}
      </Page>
    </Document>
  );
}
